import { OtherLabel } from '@ardoq/graph';
import {
  OTHER_LABEL_MAX_LINES,
  OTHER_LABEL_LINE_HEIGHT,
  OTHER_LABEL_GAP_FACTOR,
} from '../consts';
import { getLabelLinesCount, measureOtherLabelWidth } from '../../utils';

type LabelData = {
  labelData: OtherLabel;
  naturalTitleWidth: number;
  naturalCaptionWidth: number;
  hasTitle: boolean;
  popoverContent: { label: string; title: string };
};

const collectLabelsData = (otherLabels: OtherLabel[]): LabelData[] => {
  return otherLabels.map(labelData => {
    const [title, caption, showTitle] = labelData;
    const naturalTitleWidth = showTitle ? measureOtherLabelWidth(title) : 0;
    const naturalCaptionWidth = measureOtherLabelWidth(caption);

    return {
      labelData,
      naturalTitleWidth,
      naturalCaptionWidth,
      hasTitle: Boolean(showTitle),
      popoverContent: { label: caption, title },
    };
  });
};

const calculateOptimalColumnWidths = (
  labelsData: LabelData[],
  availableSpace: number,
  hasOtherLabelsWithTitle?: boolean
) => {
  // Rules for width calculation:
  // - Titles and captions of the label are arranged in columns
  // - The column cannot be wider than the widest value
  // - Titles can occupy less than 45% of the label space only if all of them fit within it without truncation
  // - Captions can occupy less than 50% of the space only if all of them fit without truncation
  // - If the label has a long caption and no title, the caption will stretch the caption column,
  //   occupying up to 50% of the space

  // If there are no labels with titles, allocate all space to captions
  if (!hasOtherLabelsWithTitle) {
    return {
      titleColumnWidth: 0,
      captionColumnWidth: availableSpace,
    };
  }

  const gap = availableSpace * OTHER_LABEL_GAP_FACTOR;
  const titleWidthThreshold = availableSpace * 0.45; // Titles should not exceed 45% of space
  const captionWidthThreshold = availableSpace * 0.5; // Captions should not exceed 50% of space

  let guaranteedTitleWidth = 0; // Minimum width needed to fit titles without truncation
  let maxRequiredCaptionWidth = 0; // Maximum width needed for captions across all labels

  // Calculate the maximum width of any title
  const maxNaturalTitleWidth = Math.max(
    0,
    ...labelsData
      .filter(item => item.hasTitle)
      .map(item => item.naturalTitleWidth)
  );

  // Find the minimum width to guarantee that titles fit without truncation
  // (but only if they're within the threshold)
  labelsData
    .filter(
      item => item.hasTitle && item.naturalTitleWidth <= titleWidthThreshold
    )
    .forEach(item => {
      guaranteedTitleWidth = Math.ceil(
        Math.max(guaranteedTitleWidth, item.naturalTitleWidth)
      );
    });

  // Calculate the required width for each caption and track the maximum
  labelsData.forEach(item => {
    const labelSpace = availableSpace - gap;
    let captionWidth;

    if (!item.hasTitle) {
      // If there is no title, caption can take full width but not more than threshold
      captionWidth = Math.min(item.naturalCaptionWidth, captionWidthThreshold);

      maxRequiredCaptionWidth = Math.max(maxRequiredCaptionWidth, captionWidth);

      return;
    }

    const isCaptionFitsInSingleLine =
      item.naturalCaptionWidth <= captionWidthThreshold;

    // Calculate how many lines would be needed if caption is limited to threshold width
    const captionLinesCountInThresholdSpace = isCaptionFitsInSingleLine
      ? 1
      : getLabelLinesCount(
          item.labelData[1],
          captionWidthThreshold,
          OTHER_LABEL_MAX_LINES + 1
        );

    const isCaptionFitsInThresholdSpace =
      captionLinesCountInThresholdSpace <= OTHER_LABEL_MAX_LINES;

    if (isCaptionFitsInThresholdSpace) {
      // Caption fits within threshold - take only as much space as needed
      captionWidth = Math.min(item.naturalCaptionWidth, captionWidthThreshold);
    } else if (item.naturalTitleWidth >= titleWidthThreshold) {
      // Title is already large, limit caption to threshold
      captionWidth = captionWidthThreshold;
    } else if (item.naturalTitleWidth) {
      // Try to give caption more space if title is small
      captionWidth = Math.min(
        labelSpace - item.naturalTitleWidth,
        item.naturalCaptionWidth
      );
    } else {
      // Default to threshold
      captionWidth = captionWidthThreshold;
    }

    maxRequiredCaptionWidth = Math.max(maxRequiredCaptionWidth, captionWidth);
  });

  // Calculate final column widths
  const spaceForTitle = availableSpace - gap - maxRequiredCaptionWidth;

  // Title column gets the smallest value among:
  // - available space after accommodating captions
  // - maximum natural title width
  // But must be at least the guaranteed title width
  const titleColumnWidth = Math.max(
    Math.min(spaceForTitle, maxNaturalTitleWidth),
    guaranteedTitleWidth
  );

  // Caption column gets the remaining space
  const captionColumnWidth = Math.round(
    availableSpace - titleColumnWidth - gap
  );

  return {
    titleColumnWidth,
    captionColumnWidth,
  };
};

type GetOtherLabelsRenderingDataArgs = {
  otherLabels?: OtherLabel[];
  availableSpace: number;
};

export const getOtherLabelsRenderingData = ({
  otherLabels,
  availableSpace,
}: GetOtherLabelsRenderingDataArgs) => {
  if (!otherLabels?.length) {
    return null;
  }

  const hasOtherLabelsWithTitle = Boolean(
    otherLabels?.some(labelData => labelData[2])
  );

  const collectedData = collectLabelsData(otherLabels);

  const { titleColumnWidth, captionColumnWidth } = calculateOptimalColumnWidths(
    collectedData,
    availableSpace,
    hasOtherLabelsWithTitle
  );

  const labelsData = collectedData.map(item => {
    const isTitleTruncated = item.naturalTitleWidth > titleColumnWidth;

    const captionText = item.labelData[1];

    const captionLinesCountOverLimit = getLabelLinesCount(
      captionText,
      captionColumnWidth,
      OTHER_LABEL_MAX_LINES + 1
    );

    const isCaptionTruncated =
      captionLinesCountOverLimit > OTHER_LABEL_MAX_LINES;

    const captionLinesCount = Math.min(
      captionLinesCountOverLimit,
      OTHER_LABEL_MAX_LINES
    );

    return {
      labelData: item.labelData,
      popoverContent: item.popoverContent,
      captionWidth: captionColumnWidth,
      captionTakesFullWidth: !hasOtherLabelsWithTitle,
      isTruncated: isCaptionTruncated || isTitleTruncated,
      height: captionLinesCount * OTHER_LABEL_LINE_HEIGHT,
    };
  });

  return {
    labelsData,
    commonTitleWidth: titleColumnWidth,
    commonMaxCaptionWidth: captionColumnWidth,
  };
};
